<template>
  <div class="frame-wrapper water-show">
    <!-- メッセージ -->
    <div class="sticky-message" v-if="stickyMessage">
      {{ stickyMessage }}
    </div>
    <!-- Floating Action Button -->
    <div class="tool-buttons" v-if="isShowMode">
      <div class="add-sensor" @click="changeToAddMode"></div>
    </div>
    <!-- マーカー移動時のボタン -->
    <div v-if="markerDragged" class="centered-box-on-map save-marker-buttons">
      <div class="centered-item">
        <div class="button-area">
          <span v-if="isAddMode" class="btn btn-submit" @click="openForm">
            入力する
          </span>
          <span v-else class="btn btn-submit" @click="updatePosition">
            位置を保存
          </span>
          <span class="btn" @click="undoDraggingMarker">キャンセル</span>
        </div>
      </div>
    </div>
    <!-- マーカー詳細編集 -->
    <div v-if="showForm" class="center-frame-container">
      <transition name="scale" @after-leave="hideForm" appear>
        <div class="center-frame">
          <div class="title-area">
            <div></div>
            <div class="btn-close" @click="hideForm"></div>
          </div>

          <validation-observer ref="obs" v-slot="ObserverProps">
            <section class="section">
              <div class="container">
                <dl class="columns">
                  <dt>センサーの製品ID</dt>
                  <dd v-if="isAddMode">
                    <ValidationProvider rules="required|max:100" v-slot="ProviderProps">
                      <input v-model="form.sensor_id" type="text" />
                      <div class="validation-error">
                        {{ ProviderProps.errors[0] }}
                      </div>
                    </ValidationProvider>
                    <span style="font-size:14px;">※購入したセンサーに記載されているIDを入力してください</span>
                  </dd>
                  <dd v-else>
                    <span>{{ form.sensor_id }}</span>
                  </dd>
                  <dt>名前</dt>
                  <dd>
                    <ValidationProvider rules="max:100" v-slot="ProviderProps">
                      <input v-model="form.sensor_name" type="text" />
                      <div class="validation-error">
                        {{ ProviderProps.errors[0] }}
                      </div>
                    </ValidationProvider>
                    <span style="font-size:14px">※センサーに識別するための名前を任意で指定できます</span>
                  </dd>
                </dl>
              </div>
            </section>
            <div class="submit-error">
              {{ errorMessage }}
            </div>

            <div class="button-area">
              <div class="left-box" />
              <div class="center-box">
                <span
                  class="btn btn-submit"
                  :class="{ disable: ObserverProps.invalid }"
                  @click="onSubmit"
                >
                  保存</span
                >
                <span class="btn btn-cancel" @click="hideForm">
                  キャンセル</span
                >
              </div>
              <div class="right-box">
                <span
                  class="text"
                  :class="{ active: anyInput && !isAddMode }"
                  @click="onDelete"
                >
                  削除
                </span>
              </div>
            </div>
          </validation-observer>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GoogleMaps from "@/mixins/googlemaps.js";
import Loader from "@/mixins/loader";
import Dialog from "@/mixins/dialog";
import isMobile from "@/mixins/ismobile";

// 画面モード
const MODE = {
  SHOW: 1, // 参照モード（初期状態）
  ADD: 2, // 追加モード
  MOVE: 3, // マーカー移動モード
  EDIT: 4, // 編集モード
};

export default {
  mixins: [GoogleMaps, Loader, Dialog, isMobile],

  components: {
  },

  data() {
    return {
      initialized: false,
      markers: [],
      sensorDatas: [],
      sensors: [],
      // 選択中・移動中のマーカー
      selectedMarker: null,
      // 現在の画面モード
      mode: MODE.SHOW,
      // 画面上部のメッセージ
      stickyMessage: "",
      // マーカーをドラッグ中ならtrue
      markerDragged: false,
      listenerNames: {
        clickMap: null,
        dragend: null,
      },
      // マーカー詳細フォームを表示するならtrue、消去するならfalse
      showForm: false,
      // マーカー詳細フォームのバインド変数
      form: {
        sensor_name: null,
        sensor_id: null,
      },
      errorMessage: ""
    };
  },

  computed: {
    anyInput() {
      return Object.keys(this.form).some((key) => {
        if (this.form[key]) {
          return true;
        }
      });
    },

    isShowMode() {
      return this.mode === MODE.SHOW;
    },

    isAddMode() {
      return this.mode === MODE.ADD;
    },

    isMoveMode() {
      return this.mode === MODE.MOVE;
    },
  },

  async mounted() {
    this.loading(async () => {
      this.$emit("showMap");
      try {
        // マーカー初期表示
        await this.loadMarkers();
        // マーカークリックイベントリスナー
        this.setClickListenerToMarkers();
        // 初期化終了フラグ
        this.initialized = true;

      } catch (error) {
        console.log(error);
        throw error;
      }
    });
  },

  destroyed() {
    this.clearMarkers();
    // カーソルをデフォルトに戻しておく
    this.map.setOptions({ draggableCursor: undefined });

    // リスナー削除
    if (this.listenerNames.clickMap) {
      this.google.maps.event.removeListener(this.listenerNames.clickMap);
    }
    if (this.listenerNames.dragend) {
      this.google.maps.event.removeListener(this.listenerNames.dragend);
    }

    // ドラッグ中マーカーをもとに戻す
    if (this.markerDragged) {
      this.undoDraggingMarker();
    }

    // 画面上のランドマークマーカーの操作禁止
    this.denyActionToMarkersExceptForDraggingOf(null);
    // clickableのみ再設定
    this.markers.forEach((marker) => {
      marker.setClickable(true);
    });
  },

  methods: {
    ...mapActions([
      "retrieveSensors",
      "insertSensor",
      "updateSensor",
      "deleteSensor",
    ]),
    ...mapGetters(["getSensorDataLatest"]),

    async loadMarkers() {
      // 選択状態をクリア
      this.initializeStates();
      this.sensors = await this.retrieveSensors();

      // 現在マップに残っているマーカーをクリア
      this.clearMarkers();
      // マーカー表示
      this.putMarkers();
      // マーカークリックイベントリスナー
      this.setClickListenerToMarkers();
    },

    setClickListenerToMarkers() {
      // ランドマークマーカーにクリックイベントリスナーを追加
      this.markers.forEach((marker) => {
        marker.addListener("click", () => {
          this.selectedMarker = marker;
          // フォーム表示
          this.openForm(marker);
          // 編集モード
          this.mode = MODE.EDIT;
        });
        marker.setClickable(true);
      });
    },

    putMarkers() {
      const { Marker } = this.google.maps;
      this.sensors.forEach((sensor) => {
        const lat = parseFloat(sensor.latitude);
        const lng = parseFloat(sensor.longitude);

        const marker = new Marker({
          position: { lat: lat, lng: lng },
          map: this.map,
        });
        marker.set("sensor", sensor);
        marker.setIcon(this.buildMarkerIconProps(marker));
        this.setInfoWindow(marker, sensor);

        // ドラッグ許可
        marker.setDraggable(true);
        // ドラッグ終了イベントリスナ
        this.listenerNames.dragend = marker.addListener("dragend", () => {
          this.startDraggingMarker(marker);

          if (this.mode !== MODE.MOVE) {
            this.mode = MODE.MOVE;
            this.stickyMessage =
              "ドラッグで水位センサーの位置を決めたら[位置を保存]をクリックして下さい。";
          }
        });
        this.markers.push(marker);
      });
    },
    setInfoWindow(marker, sensor) {
      var message = ["センサー製品ID:&nbsp", ""];
      message[0] += sensor.sensor_id;
      message[1] += sensor.sensor_name;
      var infowin = new this.google.maps.InfoWindow({
        content: message.join("<br>"),
      });

      // mouseoverイベントを取得するListenerを追加
      this.google.maps.event.addListener(marker, "mouseover", function () {
        infowin.open(marker.getMap(), marker);
      });

      // mouseoutイベントを取得するListenerを追加
      this.google.maps.event.addListener(marker, "mouseout", function () {
        infowin.close();
      });
    },

    buildMarkerIconProps(marker, toggleSelection = false) {
      // 選択済みのマーカーか
      let selected = this.selectedMarker === marker.get("sensor");
      if (toggleSelection) {
        // 選択を反転
        selected = !selected;
      }
      // アイコンファイル名の組み立て
      let iconName = "water01";
      if (selected) {
        iconName += "_selected";
      }

      let iconProps = { url: require("@/assets/water/" + iconName + ".png") };
      if (selected) {
        iconProps = Object.assign(iconProps, {
          // scaledSize: { width: 63, height: 63 },
          anchor: { x: 26, y: 53 },
        });
      } else {
        iconProps = Object.assign(iconProps, {
          // scaledSize: { width: 14, height: 43 },
          anchor: { x: 1, y: 43 },
        });
      }
      return iconProps;
    },

    clearMarkers() {
      this.markers.forEach((e) => {
        // Mapとの紐付けを解除しイベントリスナーを削除
        e.setMap(null);
        this.google.maps.event.clearInstanceListeners(e);
      });
      this.markers = [];
    },

    async changeToAddMode() {
      // 地図クリックでマーカーを設置できるようにする
      this.map.setOptions({ draggableCursor: "crosshair" });
      this.listenerNames.clickMap = this.map.addListener(
        "click",
        this.addMarker
      );

      // 画面上のマーカーの操作禁止
      this.denyActionToMarkersExceptForDraggingOf(null);

      this.stickyMessage = "水位センサーを登録したい位置でクリックして下さい。";

      this.mode = MODE.ADD;
    },

    denyActionToMarkersExceptForDraggingOf(marker) {
      // クリック禁止
      this.unsetClickListenerToMarkers();
      this.markers.forEach((m) => {
        // 引数marker以外はドラッグ禁止
        if (marker !== m) {
          m.setDraggable(false);
        }
      });
    },

    unsetClickListenerToMarkers() {
      // ランドマークマーカーのクリックイベントリスナー削除
      this.markers.forEach((marker) => {
        this.google.maps.event.clearListeners(marker, "click");
        marker.setClickable(false);
      });
    },

    async addMarker(e) {
      // マーカーを描画
      const marker = this.putNewMarker(e.latLng);

      // マーカーを追加したらそれ以上追加できないようにする
      if (this.listenerNames.clickMap) {
        this.google.maps.event.removeListener(this.listenerNames.clickMap);
      }

      // カーソルを戻す
      this.map.setOptions({ draggableCursor: undefined });

      // ドラッグ開始
      this.startDraggingMarker(marker);

      this.stickyMessage =
        "ドラッグで水位センサーの位置を決めたら[入力する]をクリックして下さい。";
    },

    putNewMarker(latLng) {
      // マーカー追加
      const { Marker } = this.google.maps;
      const marker = new Marker({
        position: latLng,
        map: this.map,
        draggable: true,
      });
      marker.setIcon(this.buildMarkerIconProps(marker));

      return marker;
    },

    startDraggingMarker(marker) {
      if (!this.markerDragged) {
        this.selectedMarker = marker;
        this.markerDragged = true;

        // 引数のマーカーのドラッグ以外は操作禁止
        this.denyActionToMarkersExceptForDraggingOf(marker);
      }
    },

    openForm() {
      if (this.selectedMarker) {
        // フォームの初期値を設定
        if (this.mode === MODE.ADD) {
          this.form.sensor_name = "";
          this.form.sensor_id = "";
        } else {
          const sensor = this.selectedMarker.get("sensor");
          this.form.sensor_name = sensor.sensor_name;
          this.form.sensor_id = sensor.sensor_id;
        }

        this.showForm = true;
      }
    },

    hideForm() {
      // フォームデータ初期化
      Object.keys(this.form).forEach((key) => {
        this.form[key] = null;
      });

      if (this.mode === MODE.ADD) {
        // 追加モードの場合はフォームを消すのみ
        this.showForm = false;
      } else {
        this.initializeStates();
      }
    },

    initializeStates() {
      this.showForm = false;
      // this.showAlertForm = false;

      // フォーム消去の描画に影響を与えるようなのでフォーム消去の描画後に各種フラグを初期化する
      this.$nextTick(() => {
        this.stickyMessage = "";
        this.selectedMarker = null;
        this.markerDragged = false;
        this.mode = MODE.SHOW;
      });
    },

    undoDraggingMarker() {
      if (this.selectedMarker) {
        if (this.mode === MODE.ADD) {
          // マーカーを消す
          this.releaseMarker(this.selectedMarker);
        } else {
          // マーカーの位置をもとに戻す
          const sensor = this.selectedMarker.get("sensor");
          this.selectedMarker.setPosition(
            new this.google.maps.LatLng(sensor.latitude, sensor.longitude)
          );
        }
      }
      this.initializeStates();

      // 全マーカーをクリック・ドラッグ可能に戻す
      this.setClickListenerToMarkers();
      this.markers.forEach((marker) => {
        marker.setDraggable(true);
      });
    },

    releaseMarker(marker) {
      // マーカーとMapとの紐付けを解除しイベントリスナーを削除
      marker.setMap(null);
      this.google.maps.event.clearInstanceListeners(marker);
    },


    async onSubmit() {
      this.errorMessage = "";
      this.loading(async () => {
        try {
          let reqParams;
          if (this.mode === MODE.ADD) {
            // フォームでは変更不可のデータをパラメータに用意
            reqParams = {
              lat: this.selectedMarker.getPosition().lat(),
              lon: this.selectedMarker.getPosition().lng(),
            };
          } else {
            // 元のデータ
            reqParams = this.selectedMarker.get("sensor");
          }
          // フォームデータを上書き
          Object.assign(reqParams, this.form);

          if (this.mode === MODE.ADD) {
            // 登録
            const res = await this.insertSensor(reqParams);
            if (res["result"] === '0') {
              this.sensors.push({
                sensor_name: reqParams.sensor_name,
                sensor_id: reqParams.sensor_id,
                latitude: reqParams.lat,
                longitude: reqParams.lon,
              });
            }
            else {
              this.errorMessage = res["message"];
              return;
            }
          } else {
            // 更新
            await this.updateSensor(reqParams);
            const sensor = this.sensors.find(e => e.sensor_id === this.selectedMarker.sensor_id);
            if (sensor) {
              sensor.latitude = reqParams.latitude;
              sensor.longitude = reqParams.longitude;
            }
          }

          // 編集画面消去
          this.hideForm();

          // 成功通知
          const message =
            this.mode === MODE.ADD ? "登録しました。" : "変更を保存しました。";
          this.$toasted.success(message, {
            position: "top-center",
            duration: 3000,
          });

          // 新規追加用マーカーを開放
          if (this.mode === MODE.ADD) {
            this.releaseMarker(this.selectedMarker);
          }
          // マーカーをリロード
          await this.loadMarkers();
        } catch (error) {
          alert("通信エラー");
          console.log(error);
          throw error;
        }
      });
    },

    async onDelete() {
      this.errorMessage = "";
      this.showConfirm("削除しますか？", async () => {
        this.loading(async () => {
          try {
            // 削除
            const params = this.selectedMarker.get("sensor");
            await this.deleteSensor(params);

            // 編集画面消去
            this.hideForm();

            // 成功通知
            this.$toasted.success("水位センサーを削除しました。", {
              position: "top-center",
              duration: 3000,
            });

            // マーカーをリロード
            // this.releaseMarker(this.selectedMarker);
            await this.loadMarkers();
          } catch (error) {
            alert("通信エラー");
            console.log(error);
            throw error;
          }
        });
      });
    },

    async updatePosition() {
      await this.loading(async () => {
        try {
          // 緯度経度を上書き
          const param = this.selectedMarker.get("sensor");
          param.latitude = this.selectedMarker.getPosition().lat();
          param.longitude = this.selectedMarker.getPosition().lng();

          await this.updateSensor(param);
          // const sensor = this.sensors.find(e => e.sensor_id === param.sensor_id);
          // if (sensor) {
          //   sensor.latitude = param.lat;
          //   sensor.longitude = param.lon;
          // }

          // 成功通知
          this.$toasted.success("位置を保存しました。", {
            position: "top-center",
            duration: 3000,
          });

          await this.loadMarkers();
        } catch (error) {
          alert("通信エラー");
          console.log(error);
          throw error;
        }
      });
    },

  },
};
</script>

<style lang="scss">

// $side-frame-width: 350px;

.water-show {
  &.frame-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;

    // .mobile & {
    //   height: 0;
    // }
  }

  .tool-buttons {
    display: flex;
    position: absolute;

    .add-sensor {
      display: block;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: white url("~@/assets/icons/ic_add_active.svg") no-repeat
        center center;
      margin: 15px;
      cursor: pointer;
    }

    // .alert-sensor {
    //   display: block;
    //   width: 40px;
    //   height: 40px;
    //   border-radius: 50%;
    //   background: white url("~@/assets/icons/ic_notifications.svg") no-repeat
    //     center center;
    //   margin: 15px;
    //   cursor: pointer;
    // }
  }

  .sticky-message {
    position: absolute;
    top: 0;
    width: 100%;
    background-color: var(--purple);
    color: white;
    text-align: center;
  }

  .btn {
    display: block;
    background: white;
    font-size: 14px;
    text-align: center;
    width: 130px;
    height: 30px;
    border-radius: 15px;
    color: var(--purple);
    padding: 6px 20px;
    margin: 10px auto 10px;
    cursor: pointer;
    &.disable {
      opacity: 0.5;
      cursor: inherit;
      pointer-events: none;
    }
    &:nth-of-type(n + 2) {
      margin-left: 1rem;
    }
    &.btn-submit {
      background: var(--purple);
      color: #fff;
    }
  }

  .centered-box-on-map {
    position: fixed;
    left: calc(50% + (250px / 2));
    z-index: 1;

    .centered-item {
      position: relative;
      left: -50%;
    }

    &.save-marker-buttons {
      bottom: 0;
      margin-bottom: 1rem;
    }

    .button-area {
      display: flex;
      justify-content: flex-start;
      width: 100%;

      .btn {
        width: 170px;
      }
    }
  }

  .center-frame-container {
    position: absolute;
    //   display: none;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100vh;
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.5);
    z-index: 90;

    .center-frame {
      margin: 0;
      padding: 15px 30px;
      width: 80%;
      max-width: 800px;
      //   height: 80vh;
      max-height: 80vh;
      overflow-y: scroll;
      position: absolute;
      top: 10vh;
      background: white;
      border-radius: 20px;
      box-shadow: 0 0 30px 15px rgba(0, 0, 0, 0.3);
      // animation: scaleAnimIn 0.5s;
    }

    //   &.active {
    //     display: flex;
    //     /* animation: scaleAnimIn 0.3s ease-out; */
    //   }

    .title-area {
      width: 100%;
      height: 80px;
      padding: 0;
      box-sizing: border-box;
      display: grid;
      grid-template-columns: 1fr 32px;
      align-items: center;
      border-bottom: solid 1px #ddd;
    }

    .btn-close {
      cursor: pointer;
      width: 32px;
      height: 32px;
      background: url("~@/assets/icons/ic_cancel.svg") no-repeat center center;
      background-size: contain;
    }

    .button-area {
      display: flex;
      justify-content: flex-start;
      width: 100%;

      .right-box {
        right: 0;
        bottom: 0;
        width: 70px;
      }
      .center-box {
        display: inline;
        text-align: center;
        width: calc(100% - 140px);
      }
      .left-box {
        left: 0;
        bottom: 0;
        width: 70px;
      }

      .btn {
        display: inline-block;
        background: white;
        font-size: 14px;
        text-align: center;
        width: 130px;
        height: 30px;
        border-radius: 15px;
        color: var(--purple);
        padding: 6px 20px;
        margin: 10px auto 10px;
        cursor: pointer;
        &.disable {
          opacity: 0.5;
          cursor: inherit;
        }
        &.btn-submit {
          background: var(--purple);
          color: #fff;
          margin-left: 3px;
          margin-right: 3px;
        }
        &.btn-cancel {
          background: var(--purple-tertiary);
          color: #000;
          margin-left: 3px;
          margin-right: 3px;
        }
      }

      .text {
        right: 0;
        bottom: 0;
        display: block;
        font-size: 14px;
        padding: 3px 0px;
        margin: 10px 0;
        text-align: center;
        color: var(--purple);
        border-bottom: solid 4px transparent;
        opacity: 0.5;
        pointer-events: none;
      }
      .text.active {
        border-bottom: solid 4px var(--purple-secondary);
        opacity: 1;
        cursor: pointer;
        pointer-events: inherit;
      }
      .text::before {
        white-space: pre;
        content: "    ";
        background: url("~@/assets/icons/ic_delete.svg") no-repeat center left;
        background-size: 20px 20px;
        padding: 0 0 0 5px;
      }
    }

    .section {
      overflow: auto;
      width: 100%;
      height: calc(100% - 130px);
      position: relative;

      .container {
        margin: 0;

        dl.columns {
          width: 100%;
          display: grid;
          grid-template-columns: 170px 1fr;
          align-items: top;
          grid-gap: 0px 0px;
          margin: 0;
          padding: 0;
          font-size: 16px;
          color: #333;
        }

        .columns dt {
          display: block;
          text-align: right;
          width: 100%;
          margin: 0;
          padding: 16px;
        }

        .columns dd {
          display: block;
          width: 100%;
          border-left: solid 1px #ddd;
          margin: 0;
          padding: 16px;

          .input-with-unit {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            .unit {
              margin-left: 1rem;
              word-break: keep-all;
            }
            input {
              width: auto;
              flex-grow: 2;
            }
          }

          span.textarea {
            white-space: pre-line;
          }

          // .image-radio-button-group {
          //   display: flex;
          //   flex-flow: wrap;
          //   .group-name {
          //     width: 100%;
          //   }
          //   &:nth-child(n + 2) {
          //     .group-name {
          //       margin-top: 0.5rem;
          //     }
          //   }
          //   .image-radio-button {
          //     text-align: center;
          //     border: 1px solid #ccc;
          //     border-radius: 5px;
          //     padding: 10px;
          //     margin: 2px;
          //   }
          //   .image-radio-button:hover {
          //     background: #eee;
          //   }
          //   .image-radio-button img {
          //     height: 42px;
          //     width: 42px;
          //   }
          //   .image-radio-button label {
          //     cursor: pointer;
          //   }
          // }

          // .grid-item3 {
          //   display: grid;
          //   grid-template-columns: 1fr auto 1fr;
          //   align-items: center;
          //   margin: 0;
          //   padding: 0;
          // }
        }
        // .columns dt:nth-of-type(odd) {
        //   background: #f3f3f3;
        // }
        // .columns dd:nth-of-type(odd) {
        //   background: #f3f3f3;
        // }

        .repeat {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        }
        // input[type="checkbox"]:checked ~ .repeat {
        //   display: none;
        // }
        /* フォーム */

        input[type="text"],
        input[type="date"],
        input[type="number"] {
          width: 100%;
          height: 40px;
          border-radius: 20px;
          border: solid 1px var(--purple);
          padding: 0 16px;
          font-size: 16px;
          outline: none;
        }

        input[type="text"]:focus,
        input[type="date"]:focus,
        input[type="number"]:focus {
          background: var(--purple-tertiary);
        }

        // select {
        //   width: 100%;
        //   height: 40px;
        //   border-radius: 20px;
        //   border: solid 1px var(--purple);
        //   padding: 0 38px 0 16px;
        //   font-size: 16px;
        //   outline: none;
        //   -webkit-appearance: none;
        //   -moz-appearance: none;
        //   appearance: none;
        //   background: url("~@/assets/icons/ic_dropdown.svg") no-repeat
        //     calc(100% - 10px) center;
        //   background-size: 25px 25px;
        // }

        // select:focus {
        //   background: url("~@/assets/icons/ic_dropdown.svg") no-repeat
        //     calc(100% - 10px) center var(--purple-tertiary);
        //   background-size: 25px 25px;
        // }

        textarea {
          font-size: 16px;
          width: 100%;
          min-height: 200px;
          white-space: pre-wrap;
          outline: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          border: solid 1px var(--purple);
          padding: 10px;
          border-radius: 10px 10px 0 10px;
          font-family: inherit;
        }

        textarea:focus {
          background: var(--purple-tertiary);
        }

        // input[type="radio"] {
        //   display: none;
        // }
        // input[type="radio"] + label {
        //   padding: 0 20px 0 0;
        // }
        // input[type="radio"]:disabled + label {
        //   color: #aaa;
        // }

        // input[type="radio"] + label:before {
        //   display: inline-block;
        //   white-space: pre;
        //   content: "    ";
        //   background: url("~@/assets/icons/ic_radio_off.svg") no-repeat center
        //     left;
        //   background-size: 24px 24px;
        //   padding: 0 16px 0 0;
        // }
        // input[type="radio"]:checked + label:before {
        //   white-space: pre;
        //   content: "    ";
        //   background: url("~@/assets/icons/ic_radio_on.svg") no-repeat center
        //     left;
        //   background-size: 24px 24px;
        //   display: inline-block;
        // }
        // input[type="radio"]:disabled + label:before {
        //   white-space: pre;
        //   content: "    ";
        //   background: url("~@/assets/icons/ic_radio_disabled.svg") no-repeat
        //     center left;
        //   background-size: 24px 24px;
        //   display: inline-block;
        // }

        // input[type="checkbox"] {
        //   display: none;
        // }
        // input[type="checkbox"] + label {
        //   padding: 0 20px 0 0;
        // }

        // input[type="checkbox"] + label:before {
        //   display: inline-block;
        //   white-space: pre;
        //   content: "    ";
        //   background: url("~@/assets/icons/ic_checkbox_off.svg") no-repeat
        //     center left;
        //   background-size: 24px 24px;
        //   padding: 0 16px 0 0;
        // }
        // input[type="checkbox"]:checked + label:before {
        //   white-space: pre;
        //   content: "    ";
        //   background: url("~@/assets/icons/ic_checkbox_on.svg") no-repeat center
        //     left;
        //   background-size: 24px 24px;
        //   display: inline-block;
        // }
      }
    }
  }

  .top-frame-container {
    width: 100%;
    max-width: 510px;
    //   padding: 30px;
    position: absolute;
    top: 30px;
    left: 30px;
    transition: height 0.5s;

    // .mobile & {
    //   top: 50px;
    //   left: 0;
    //   // padding: 10px;
    //   bottom: 0;
    //   z-index: 100;

    //   // .filter:active {
    //   //   display: block;
    //   // }
    // }

    .top-frame {
      // width: 100%;
      // max-width: 480px;
      // min-width: 350px;
      min-height: 100px;
      padding: 20px;
      // margin-right: $side-frame-width;
      background: white;
      box-sizing: border-box;
      border-radius: 20px;
      box-shadow: 0 0 30px 15px rgba(0, 0, 0, 0.3);
      transition: height 0.5s;
    }

    .top-frame .title-area {
      border-bottom: none;
      display: grid;
      grid-template-columns: 1fr 200px;
      align-items: center;
      margin: 0;
      padding: 0;

      h3 {
        background: url("~@/assets/icons/ic_water_temp.svg") no-repeat left
          center;
        background-size: 30px 27px;
      }
    }
    .top-frame h3 {
      padding: 0 0 0 2em;
      margin: 0px 15px 15px 15px;

      .mobile & {
        margin: 0px;
      }
    }
    .top-frame .selectCal {
      text-align: right;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      user-select: none;
    }

    .top-frame .selectCal:active {
      opacity: 0.5;
      /* transition: 0.5s; */
    }

    .arrow-left {
      display: block;
      width: 16px;
      height: 16px;
      background: url("~@/assets/icons/ic_arrow.svg") no-repeat center center;
      background-size: 16px 16px;
      margin: 0 10px 0 0;
      cursor: pointer;
    }

    .arrow-left:active {
      transform: scale(1.2);
    }

    .arrow-right {
      display: block;
      width: 16px;
      height: 16px;
      background: url("~@/assets/icons/ic_arrow.svg") no-repeat center center;
      background-size: 16px 16px;
      transform: rotate(180deg);
      margin: 0 0 0 10px;
      cursor: pointer;
    }

    .arrow-right:active {
      transform: scale(1.2);
    }

    .top-frame .filter {
      text-align: right;
      display: block;
      cursor: pointer;
      font-size: 14px;
      margin: 0 20px 0 0;
      padding: 0;
      text-decoration: none;
      color: var(--purple);
    }

    .top-frame .filter::before {
      white-space: pre;
      content: "    ";
      background: url("~@/assets/icons/ic_sort.svg") no-repeat center center;
      background-size: 20px 20px;
      padding: 0 0 0 20px;
    }

    .top-frame .filter:active {
      opacity: 0.5;
    }

    // input[type="radio"] {
    //   display: none;
    // }

    // input[type="checkbox"] {
    //   display: none;
    // }

    // .input-frame-wrap {
    //   max-height: 500px;
    //   display: block;
    //   overflow: hidden;
    //   transition: ease-in-out 0.3s;
    // }

    // .input-frame {
    //   display: grid;
    //   grid-template-columns: repeat(auto-fill, 120px);
    //   justify-content: center;
    //   grid-row-gap: 10px;
    //   grid-column-gap: 10px;
    //   user-select: none;
    //   margin: 15px 0 0 0;
    // }
    // .input-frame-wrap.close {
    //   max-height: 0px;
    //   transition: ease-in-out 0.3s;
    // }

    // .input-frame-B {
    //   display: grid;
    //   justify-content: center;
    //   grid-template-columns: repeat(auto-fill, 120px);
    //   grid-row-gap: 10px;
    //   grid-column-gap: 10px;
    //   user-select: none;
    //   background: #eee;
    //   padding: 20px;
    //   margin: 20px 0 0 0;
    //   border-radius: 20px;
    //   border: solid 1px #ccc;
    // }

    .label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    // input[type="radio"]:checked + .col1 {
    //   background: var(--col1);
    //   color: #fff;
    // }

    // input[type="radio"]:checked + .col2 {
    //   background: var(--col2);
    //   color: #fff;
    // }
    // input[type="radio"]:checked + .col3 {
    //   background: var(--col3);
    //   color: #fff;
    // }
    // input[type="radio"]:checked + .col4 {
    //   background: var(--col4);
    //   color: #fff;
    // }
    // input[type="radio"]:checked + .col5 {
    //   background: var(--col5);
    //   color: #fff;
    // }
    // input[type="checkbox"]:checked + .col1 {
    //   background: var(--col1);
    //   color: #fff;
    // }

    // input[type="checkbox"]:checked + .col2 {
    //   background: var(--col2);
    //   color: #fff;
    // }
    // input[type="checkbox"]:checked + .col3 {
    //   background: var(--col3);
    //   color: #fff;
    // }
    // input[type="checkbox"]:checked + .col4 {
    //   background: var(--col4);
    //   color: #fff;
    // }
    // input[type="checkbox"]:checked + .col5 {
    //   background: var(--col5);
    //   color: #fff;
    // }
    // .item-summary {
    //   display: flex;
    //   justify-content: space-around;
    // }
    // .select-button {
    //   display: block;
    //   width: 200px;
    //   margin: 20px auto 0px;
    //   text-align: center;
    //   font-size: 14px;
    //   padding: 10px 20px;
    //   border-radius: 20px;
    //   background: var(--purple);
    //   color: #fff;
    //   cursor: pointer;
    //   user-select: none;
    //   &#selectFlagButton::before {
    //     white-space: pre;
    //     content: "    ";
    //     margin: 0 10px 0 0;
    //     background: url("~@/assets/icons/ic_expand.svg") no-repeat center center;
    //   }
    //   &:active {
    //     opacity: 0.5;
    //   }
    //   &#selectItemButton {
    //     margin: 0;
    //     width: 170px;
    //   }
    // }
    // .navigate-button {
    //   display: block;
    //   width: 200px;
    //   margin: 0px auto 0px;
    //   text-align: center;
    //   font-size: 14px;
    //   padding: 10px 20px 0px 20px;
    //   border-radius: 20px;
    //   background: transparent;
    //   color: #000;
    //   cursor: default;
    //   user-select: none;
    //   &::before {
    //     white-space: pre;
    //     content: "    ";
    //     margin: 0 10px 0 0;
    //     background: url("~@/assets/icons/ic-expand-more.svg") no-repeat center
    //       center;
    //   }
    // }

    // .selection-frame {
    //   display: grid;
    //   grid-template-columns: 120px 120px;
    //   grid-column-gap: 10px;
    //   user-select: none;
    //   margin: 7px 0 7px 0;
    // }
  }

  // .col1 {
  //   display: inline-block;
  //   text-align: center;
  //   color: var(--col1);
  //   border: 1px solid var(--col1);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }
  // .col1-fill {
  //   display: inline-block;
  //   text-align: center;
  //   color: white;
  //   background: var(--col1);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }

  // .col2 {
  //   display: inline-block;
  //   text-align: center;
  //   color: var(--col2);
  //   border: 1px solid var(--col2);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }

  // .col2-fill {
  //   display: inline-block;
  //   text-align: center;
  //   color: white;
  //   background: var(--col2);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }

  // .col3 {
  //   display: inline-block;
  //   text-align: center;
  //   color: var(--col3);
  //   border: 1px solid var(--col3);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }
  // .col3-fill {
  //   display: inline-block;
  //   text-align: center;
  //   color: white;
  //   background: var(--col3);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }

  // .col4 {
  //   display: inline-block;
  //   text-align: center;
  //   color: var(--col4);
  //   border: 1px solid var(--col4);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }
  // .col4-fill {
  //   display: inline-block;
  //   text-align: center;
  //   color: white;
  //   background: var(--col4);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }

  // .col5 {
  //   display: inline-block;
  //   text-align: center;
  //   color: var(--col5);
  //   border: 1px solid var(--col5);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }
  // .col5-fill {
  //   display: inline-block;
  //   text-align: center;
  //   color: white;
  //   background: var(--col5);
  //   padding: 4px;
  //   cursor: pointer;
  //   font-size: 13px;
  //   border-radius: 30px;
  // }

  // // TODO 必要？
  // .side-frame-container {
  //   position: absolute;
  //   top: 30px;
  //   right: 30px;
  //   width: $side-frame-width;
  //   min-width: 270px;
  //   max-width: 450px;
  //   min-height: 100px;
  //   //   padding: 30px 30px 30px 0;
  //   width: 100%;
  //   // padding: 30px 30px 30px 0;

  //   .mobile & {
  //     top: 0px;
  //     right: 0px;
  //     // padding: 10px 10px 40px 10px;
  //     z-index: 100;
  //   }

  //   .side-frame {
  //     // width: 100%;
  //     // min-width: 270px;
  //     // max-width: 450px;
  //     // min-height: 100px;
  //     padding: 0px;
  //     background: white;
  //     box-sizing: border-box;
  //     border-radius: 20px;
  //     box-shadow: 0 0 30px 15px rgba(0, 0, 0, 0.3);
  //     .mobile & {
  //       border-radius: unset;
  //     }
  //   }
  //   #side-frame-water-header {
  //     // display: flex;
  //     align-items: center;
  //     width: 100%;
  //     // height: 60px;
  //     padding: 10px;
  //     background: #fff;
  //     border-radius: 20px 20px 0 0;
  //     box-sizing: border-box;
  //     border-bottom: solid 1px #eee;

  //     h4 {
  //       display: inline-block;
  //       // width: 40%;
  //       font-size: 20px;
  //       text-align: left;
  //       font-weight: bold;
  //       margin: 0;
  //       padding: 0 0 0 0;
  //     }
  //     span {
  //       display: inline-block;
  //       width: 100%;
  //       font-size: 13px;
  //       text-align: right;
  //       font-weight: normal;
  //       margin: 0;
  //       padding: 0 10px 0 30px;
  //       color: #666;
  //     }
  //     img {
  //       display: inline-block;
  //       // width: 20px;
  //       margin: 0 10px 0 0;
  //       height: 40px;
  //     }

  //     &.close {
  //       height: 0px;
  //       transition: ease-in-out 0.3s;
  //     }

  //     h4::before {
  //       content: "";
  //     }
  //   }

  //   // #side-frame-footer {
  //   //   width: 100%;
  //   //   padding: 5px;
  //   //   border-radius: 0 0 20px 20px;
  //   //   background: #ddd;
  //   // }
  //   #side-frame-water-footer {
  //     width: 100%;
  //     height: 40px;
  //     padding: 5px;
  //     border-radius: 0 0 20px 20px;
  //     background: #fff;
  //     border-top: solid 1px #eee;
  //   }

  //   .side-frame h4 {
  //     font-size: 20px;
  //     text-align: center;
  //     font-weight: normal;
  //     margin: 0px auto 0;
  //     // padding: 0 0 0 2em;
  //   }
  //   .side-frame h4::before {
  //     white-space: pre;
  //     content: "    ";
  //     background: url("~@/assets/icons/ic_list.svg") no-repeat center center;
  //     margin: 0 10px 0 0;
  //   }

  //   .side-frame-content {
  //     // 170px＝サイドフレームの上下padding＋ヘッダ＋フッタ
  //     max-height: calc(100vh - 170px);
  //     box-sizing: border-box;
  //     overflow: scroll;

  //     .mobile & {
  //       max-height: calc(100vh - 120px);
  //     }
  //   }

  //   #title-area-water {
  //     width: 100%;
  //     margin: 0;
  //     padding: 15px 20px;
  //     border-bottom: solid 1px #eee;
  //     box-sizing: border-box;
  //     display: grid;
  //     grid-template-columns: 1fr 30px;
  //     h3 {
  //       margin: 0;
  //       padding: 0;
  //       font-size: 1.2rem;
  //     }
  //     p {
  //       margin: 0;
  //       padding: 0;
  //       font-size: 0.8rem;
  //       color: #888;
  //     }
  //     img {
  //       display: inline-block;
  //       margin: 0 10px 3px 0;
  //       height: 26px;
  //     }
  //     span {
  //       white-space: pre;
  //       content: "    ";
  //       background: url("~@/assets/icons/ic_cancel.svg") no-repeat center;
  //       width: 26px;
  //       height: 26px;
  //       background-size: contain;
  //       vertical-align: middle;
  //       display: inline-block;
  //     }
  //   }
  // }

  .water01 {
    background: url("~@/assets/icons/ic_thermometer.svg") no-repeat center
      center;
    background-size: 30px 30px;
  }

  .water02 {
    background: url("~@/assets/icons/ic_thermometer.sun.svg") no-repeat center
      center;
    background-size: 30px 30px;
  }

  // #side-frame-water-footer {
  //   width: 100%;
  //   height: 40px;
  //   padding: 5px;
  //   border-radius: 0 0 20px 20px;
  //   background: #fff;
  //   border-top: solid 1px #eee;
  // }

  // .side-frame-content water {
  //   background: #eee;
  // }
  // .graph {
  //   display: block;
  //   width: 90%;
  //   /* padding: 10px; */
  //   /* height: 200px; */
  //   border-radius: 10px;
  //   background: #fff;
  //   margin: 20px auto 20px;
  //   padding: 20px;
  //   box-shadow: 0 0 10px 4px rgba(0, 0, 0, 0.1);
  // }

  // .graph-header {
  //   width: 95%;
  //   height: 40px;
  //   margin: 0 auto 0;
  //   padding: 0;
  //   display: grid;
  //   grid-template-columns: 30px 1fr 100px;
  //   justify-content: space-between;
  //   align-items: center;
  //   box-sizing: border-box;
  // }

  // .graph-header h5 {
  //   display: block;
  //   font-size: 16px;
  //   margin: 0;
  //   padding: 0 0 0 5px;
  // }
  // .graph-header span {
  //   display: block;
  //   height: 40px;
  // }
  // .graph-header p {
  //   font-size: 22px;
  //   font-weight: bold;
  //   color: #888;
  //   margin: 0;
  //   padding: 0;
  //   text-align: right;
  // }

  // .graph-graph {
  //   display: block;
  //   width: 95%;
  //   height: 200px;
  //   margin: 10px auto 0px;
  //   border-radius: 10px;
  //   background: #eee;
  // }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .scale-enter,
  .scale-leave-to {
    transform: scale(0.5);
    opacity: 0;
  }
  .scale-enter-active {
    transition: transform 0.25s, opacity 0.25s;
    transition-timing-function: ease-out;
  }
  .scale-leave-active {
    transition: transform 0.25s, opacity 0.25s;
    transition-timing-function: ease-in;
  }

  /* アニメーション */

  @keyframes scaleAnimIn {
    0% {
      display: flex;
      transform: scale(0.5);
    }
    100% {
      display: flex;
      transform: scale(1);
    }
  }

  @keyframes fadeAnimIn {
    0% {
      display: grid;
      opacity: 0;
    }
    100% {
      display: grid;
      opacity: 1;
    }
  }

  #info {
    position: fixed;
    align-items: center;
    background: rgba(83, 54, 155, 0.8);
    margin: 48px 0 0 0;
    width: 100%;
    height: 33px;
    text-align: center;
    color: #fff;
    font-size: 0.7rem;
    display: none;
    opacity: 0;
    transition: opacity 0.3s;
  }

  #info.active {
    display: grid;
    z-index: 21;
    opacity: 1;
  }

  // #warter-filter {
  //   position: fixed;
  //   align-items: center;
  //   background: rgba(83, 54, 155, 0.8);
  //   margin: 68px 0 0 0;
  //   width: 100%;
  //   height: 33px;
  //   text-align: right;
  //   color: #fff;
  //   font-size: 0.7rem;
  //   display: none;
  //   opacity: 0;
  //   transition: opacity 0.3s;
  // }

  // #select-box {
  //   width: 90%;
  //   height: 30px;
  //   color: #333;
  //   font-size: 0.8rem;
  //   text-align: center;
  //   position: fixed;
  //   bottom: 65px;
  //   left: 50%;
  //   transform: translateX(-50%);
  //   background: #fff;
  //   display: none;
  //   align-items: center;
  //   border-radius: 15px;
  //   box-shadow: 0 0 10px 0px rgba(0, 0, 0, 0.3);
  //   z-index: 20;

  //   span {
  //     text-overflow: ellipsis;
  //     overflow: hidden;
  //     height: 20px;
  //     margin: 0px 10px;
  //   }
  // }

  // #select-box.active {
  //   display: grid;
  // }

  // #panel3 {
  //   height: 60vh;
  //   /* heightはslidwInの100%の値と同じにする */
  //   width: calc(100% - 12px);
  //   background: #fff;
  //   position: absolute;
  //   bottom: 0;
  //   left: 50%;
  //   transform: translateX(-50%);
  //   border-radius: 20px 20px 0 0;
  //   box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.8);
  //   z-index: 100;
  //   display: none;
  // }

  // #panel3.active {
  //   animation: slideIn 0.4s ease;
  //   display: block;
  // }

  // #panel3-header {
  //   display: grid;
  //   grid-template-columns: 1fr auto;
  //   align-items: center;
  //   justify-content: space-between;
  //   margin: 0;
  //   padding: 0 15px;
  //   border-radius: 20px 20px 0 0;
  //   height: 60px;
  //   width: 100%;
  //   line-height: 60px;
  //   box-sizing: border-box;
  //   border-bottom: solid 1px #ddd;
  // }

  // #panel3-header h3 {
  //   height: 60px;
  //   font-size: 0.9rem;
  //   font-weight: bold;
  //   margin: 0;
  //   padding: 0;
  //   box-sizing: border-box;
  // }

  // #panel3-header h3.style-black::before {
  //   content: "";
  //   display: inline-block;
  //   width: 33px;
  //   height: 28px;
  //   background: url("~@/assets/icons/ic_water_level.svg") no-repeat left -2px;
  //   background-size: contain;
  //   vertical-align: middle;
  // }

  // #panel3-header .select-cal {
  //   display: grid;
  //   grid-template-columns: 16px 1fr 16px;
  //   justify-content: center;
  //   align-items: center;
  //   gap: 8px;
  //   cursor: pointer;
  //   user-select: none;
  //   font-size: 0.85rem;
  //   text-align: center;
  // }

  // #panel3-header .arrow-left {
  //   display: block;
  //   width: 16px;
  //   height: 16px;
  //   background: url("~@/assets/icons/ic_arrow.svg") no-repeat center center;
  //   background-size: 16px 16px;
  //   cursor: pointer;
  // }

  // #panel3-header .arrow-right {
  //   display: block;
  //   width: 16px;
  //   height: 16px;
  //   background: url("~@/assets/icons/ic_arrow.svg") no-repeat center center;
  //   background-size: 16px 16px;
  //   transform: rotate(180deg);
  //   cursor: pointer;
  // }

  #water-next-btn {
    width: 100%;
    height: 50px;
    text-align: center;
    background: rgba(83, 54, 155, 0.8);
    position: fixed;
    bottom: 0;
    left: 0;
    display: none;
    align-items: center;
    cursor: pointer;
    z-index: 20;
  }

  #water-next-btn.active {
    display: grid;
  }

  #water-next-btn span {
    color: #fff;
    font-size: 1rem;
  }

  #water-next-btn span::before {
    white-space: pre;
    content: "    ";
    display: inline-block;
    background: url("~@/assets/icons/ic_next.svg") no-repeat left -2px;
    width: 26px;
    height: 20px;
    background-size: contain;
    vertical-align: middle;
  }
  #water-submit-btn {
    width: 200px;
    color: #fff;
    font-size: 0.8rem;
    text-align: center;
    background: var(--purple);
    margin: 30px auto 0;
    padding: 8px 0;
    border-radius: 20px;
    cursor: pointer;
  }
  // #input input[type="checkbox"] {
  //   display: none;
  // }
  /* input[type="checkbox"] + label {
  padding: 0 20px 0 0;
} */

  // #input input[type="checkbox"] + label:before {
  //   white-space: pre;
  //   content: "    ";
  //   background: url("~@/assets/icons/ic_checkbox_off.svg") no-repeat center left;
  //   width: 25px;
  //   height: 20px;
  //   background-size: contain;
  //   vertical-align: middle;
  //   display: inline-block;
  // }
  // #input input[type="checkbox"]:checked + label:before {
  //   white-space: pre;
  //   content: "    ";
  //   background: url("~@/assets/icons/ic_checkbox_off.svg") no-repeat center left;
  //   width: 25px;
  //   height: 20px;
  //   background-size: contain;
  //   vertical-align: middle;
  //   display: inline-block;
  // }

  // #edit-area {
  //   // display: flex;
  //   // justify-content: flex-start;
  //   width: 60px;
  //   // position: fixed;
  //   text-align: center;
  //   background: rgba(256, 256, 256);
  //   margin: 90px 10px 10px auto;
  //   height: 60px;
  //   border-radius: 40px;
  // }
  // #edit-area span {
  //   display: block;
  //   font-size: 28px;
  //   // padding: 3px 0px;
  //   // margin: 10px 0;
  //   // text-align: center;
  //   color: var(--purple);
  //   // margin: 8px 15px;
  //   padding: 10px;
  // }

  // #edit-area span.active {
  //   // border-bottom: solid 4px var(--purple);
  //   cursor: pointer;
  // }

  // #edit-area span::before {
  //   white-space: pre;
  //   content: "    ";
  //   background: url("~@/assets/icons/ic_edit.svg") no-repeat center center;
  //   // background-size: 20px 20px;
  //   // padding: 0 0 0 15px;
  // }

  .submit-error {
    text-align: center;
    color: var(--col1);
    margin: 0 10px;
  }
}
</style>
